<template>
  <section>
    <div class="popups">
      <b-sidebar
        v-model="showFilters"
        right
        shadow
        backdrop
        bg-variant="white"
        no-header
      >
        <template #default="{ hide }">
          <div class="container sidebar-header">
            <b-row>
              <div class="col">
                <b-button
                  variant="primary"
                  @click="
                    applyFilters();
                    loadItems();
                    showFilters = false;
                  "
                  >Apply</b-button
                >
              </div>
              <div class="col-auto text-right">
                <b-button variant="link" class="ml-3 px-0" @click="hide">
                  <i class="fa fa-times"></i>
                </b-button>
              </div>
            </b-row>
          </div>

          <div class="px-3 py-2">
            <b-form-group label="Search:">
              <b-form-input v-model="formFilters.search" />
            </b-form-group>
            <b-form-group label="Client:">
              <b-v-select
                v-model="formFilters.brandId"
                :searchable="true"
                :options="brandsFilter"
              />
            </b-form-group>
            <b-form-group label="Signed Up:">
              <b-form-select
                v-model="formFilters.userSignedUp"
                :options="yesNoFilterOptions"
              />
            </b-form-group>
          </div>
        </template>
      </b-sidebar>

      <b-modal
        v-model="showInviteModal"
        title="Invite User"
        ok-title="Invite"
        :ok-disabled="savingInvite"
        @ok.prevent="submitInvite"
      >
        <b-form-group label="First Name:">
          <b-form-input v-model="inviteForm.firstName" />
        </b-form-group>
        <b-form-group label="Last Name:">
          <b-form-input v-model="inviteForm.lastName" />
        </b-form-group>
        <b-form-group label="Email:">
          <b-form-input v-model="inviteForm.email" type="email" />
        </b-form-group>
        <b-form-group label="Client:">
          <b-v-select
            v-model="inviteForm.brandId"
            :searchable="true"
            :options="brandsList"
          />
        </b-form-group>
        <b-form-group label="Role:">
          <b-form-select
            v-model="inviteForm.roleType"
            :options="allowedRoleOptions"
          />
        </b-form-group>
      </b-modal>
    </div>

    <div class="header">
      <div class="header-body">
        <div class="row align-items-end">
          <div class="col">
            <h1 class="header-title">
              User Invites
            </h1>
          </div>
          <div class="col-auto">
            <b-button
              variant="success"
              v-if="has('pm.teams.users.invite') && allowedRoleOptions.length"
              @click="openInviteModal"
            >
              <i class="mr-2 fa fa-plus" />
              Invite
            </b-button>
            <b-button
              variant="primary"
              class="ml-2"
              @click="showFilters = !showFilters"
            >
              <i class="fa fa-filter mr-2"></i>
              Filters
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <div class="mb-3">
      <b-button-group>
        <b-button
          variant="outline-primary"
          exact
          :to="{ name: 'Admin.Manage.Users.Index' }"
        >
          Users
        </b-button>
        <b-button
          variant="primary"
          :to="{ name: 'Admin.Manage.Users.Invites' }"
          exact
        >
          User Invites
        </b-button>
      </b-button-group>
    </div>

    <TableListing
      class="mb-0"
      :loading-page="loadingItems"
      :failed-loading="failedLoadItems"
      head-variant="dark"
      :items.sync="items"
      :fields.sync="fields"
      :pagination.sync="pagination"
      :options.sync="tableOptions"
      v-on:pagination-changed="loadItems"
    >
      <template v-slot:cell(name)="data">
        <span>{{ data.item.firstName }} {{ data.item.lastName }}</span>
      </template>
      <template v-slot:cell(roleType)="data">
        <span>{{ roleLabel(data.item.roleType) }}</span>
      </template>
      <template v-slot:cell(status)="data">
        <span v-if="data.item.userSignedUp" class="badge badge-success">Signed Up</span>
        <span v-else-if="data.item.emailInviteSent" class="badge badge-info">Invite Sent</span>
        <span v-else class="badge badge-secondary">Not Sent</span>
      </template>
      <template v-slot:cell(inviteSentAtDateTimeUtc)="data">
        <span v-if="data.item.inviteSentAtDateTimeUtc">{{
          data.item.inviteSentAtDateTimeUtc | moment('MMM Do YY, h:mm a')
        }}</span>
      </template>
      <template v-slot:cell(id)="data">
        <div class="d-flex" v-if="has('pm.teams.users.invite') && allowedRoleOptions.length">
          <b-button
            variant="primary"
            class="mr-1"
            v-if="!data.item.userSignedUp"
            :disabled="resendingId === data.item.id"
            @click="resendInvite(data.item)"
          >
            <i class="fa fa-paper-plane mr-1"></i>
            Resend
          </b-button>
          <b-button
            variant="secondary"
            class="mr-1"
            v-if="!data.item.userSignedUp && data.item.inviteUrl"
            v-b-tooltip.hover
            title="Copy invite URL"
            @click="copyInviteUrl(data.item)"
          >
            <i class="fa fa-copy"></i>
          </b-button>
          <b-button
            variant="danger"
            v-if="has('pm.teams.users.remove')"
            @click="deleteInvite(data.item)"
          >
            <i class="fa fa-trash"></i>
          </b-button>
        </div>
      </template>
    </TableListing>
  </section>
</template>

<script>
import TableListing from '@/views/components/TableListing.vue';
import { crudMixinGen, myCommonDataMixin } from '@/helpers/utils/vueMixins.js';
import userInvitesApi from '@/helpers/store/modules/userInvites/apiHelpers.js';

const defaultInviteForm = () => ({
  firstName: '',
  lastName: '',
  email: '',
  brandId: '',
  roleType: 'brand-user',
  redirectToUrl: '',
  sendEmailImmediately: true
});

// rank: higher = more privileges, keep in sync with ClientRoleHierarchy.cs
const clientRoleOptions = [
  { value: 'brand-user-manager', text: 'Client: Team Manager', rank: 4 },
  { value: 'brand-user', text: 'Client: Team Member', rank: 3 },
  { value: 'brand-user-support', text: 'Client: Customer Support', rank: 2 },
  { value: 'brand-user-readonly', text: 'Client: View Only', rank: 1 }
];

export default {
  name: 'Users.Invites',
  mixins: [crudMixinGen('userInvites'), myCommonDataMixin],
  components: {
    TableListing
  },

  data: () => ({
    showInviteModal: false,
    savingInvite: false,
    resendingId: null,
    clientRoleOptions,
    inviteForm: defaultInviteForm(),
    yesNoFilterOptions: [
      { text: 'Any', value: null },
      { text: 'Yes', value: true },
      { text: 'No', value: false }
    ],
    fields: [
      { key: 'name', label: 'Name', sortable: false },
      { key: 'email', label: 'Email', sortable: true },
      { key: 'brandName', label: 'Client', sortable: false },
      { key: 'roleType', label: 'Role', sortable: false },
      { key: 'status', label: 'Status', sortable: false },
      { key: 'emailSentCount', label: 'Emails Sent', sortable: true },
      { key: 'inviteSentAtDateTimeUtc', label: 'Last Sent', sortable: true },
      { key: 'id', label: 'Action', sortable: false }
    ]
  }),

  computed: {
    // Client users can only invite the same role as theirs or a lower one, everyone else can invite any role
    allowedRoleOptions() {
      // vendor partners cannot invite anyone
      if (this.is('asn-manager')) {
        return [];
      }
      const ownRanks = clientRoleOptions
        .filter(r => this.is(r.value))
        .map(r => r.rank);
      if (!ownRanks.length) {
        return clientRoleOptions;
      }
      const maxRank = Math.max(...ownRanks);
      return clientRoleOptions.filter(r => r.rank <= maxRank);
    }
  },

  mounted() {
    this.$store.dispatch('fetchMyTPLData');
    this.loadItems();
  },

  methods: {
    roleLabel(roleType) {
      const role = clientRoleOptions.find(r => r.value === roleType);
      return role ? role.text : roleType || '-';
    },

    errorMessageFrom(error, fallback) {
      return (
        (error && error.response && error.response.data && error.response.data.errorMessage) ||
        fallback
      );
    },

    copyInviteUrl(invite) {
      const onCopied = () => {
        this.$bvToast.toast('Invite URL for ' + invite.email + ' copied to the clipboard.', {
          title: 'Copied',
          variant: 'success',
          solid: true
        });
      };
      const fallbackCopy = () => {
        const textarea = document.createElement('textarea');
        textarea.value = invite.inviteUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          onCopied();
        } catch (e) {
          this.$swal('Copy failed', invite.inviteUrl, 'warning');
        }
        document.body.removeChild(textarea);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(invite.inviteUrl)
          .then(onCopied)
          .catch(fallbackCopy);
      } else {
        fallbackCopy();
      }
    },

    openInviteModal() {
      this.inviteForm = defaultInviteForm();
      const allowed = this.allowedRoleOptions;
      if (!allowed.some(r => r.value === this.inviteForm.roleType)) {
        this.inviteForm.roleType = allowed[0].value;
      }
      this.showInviteModal = true;
    },

    submitInvite() {
      const f = this.inviteForm;
      if (!f.firstName || !f.lastName || !f.email || !f.brandId || !f.roleType) {
        this.$swal('Missing information', 'First name, last name, email, client and role are required.', 'warning');
        return;
      }
      this.savingInvite = true;
      userInvitesApi
        .addItem(f)
        .then(() => {
          this.showInviteModal = false;
          this.loadItems();
          this.$swal('Invited!', 'An invite has been created for ' + f.email + '.', 'success');
        })
        .catch(error => {
          this.$swal('Error', this.errorMessageFrom(error, 'The invite could not be created.'), 'error');
        })
        .finally(() => {
          this.savingInvite = false;
        });
    },

    resendInvite(invite) {
      this.resendingId = invite.id;
      userInvitesApi
        .resendItem(invite.id)
        .then(() => {
          this.loadItems();
          this.$swal('Sent!', 'The invite was resent to ' + invite.email + '.', 'success');
        })
        .catch(error => {
          this.$swal('Error', this.errorMessageFrom(error, 'The invite could not be resent.'), 'error');
        })
        .finally(() => {
          this.resendingId = null;
        });
    },

    deleteInvite(invite) {
      this.$swal({
        title: 'Delete invite?',
        text: 'The invite for ' + invite.email + ' will be removed.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      }).then(result => {
        if (!result.value) {
          return;
        }
        this.$store
          .dispatch('userInvites/deleteItem', invite.id)
          .then(() => {
            this.$swal('Deleted!', 'The invite has been deleted.', 'success');
          })
          .catch(error => {
            this.$swal('Error', this.errorMessageFrom(error, 'The invite could not be deleted.'), 'error');
          });
      });
    }
  }
};
</script>
